import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import useStoreSubdomain from '@/hooks/useStoreSubdomain';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  CheckCircle, Package, CreditCard, Truck, Copy, ShoppingBag, ArrowLeft,
  Clock, Shield, Phone, Mail, MapPin, Loader2, Upload, Heart,
  FileText, AlertCircle, Star, Gift, ChevronRight, Video, Calendar, User
} from 'lucide-react';
import { toast } from 'sonner';

export default function ThankYou() {
  const [searchParams] = useSearchParams();
  const subdomain = useStoreSubdomain();
  const orderNumber = searchParams.get('order');
  const donationId = searchParams.get('donation');
  const ppobOrderId = searchParams.get('ppob_order');
  const type = searchParams.get('type') || 'order'; // 'order', 'donation', or 'ppob'

  const [store, setStore] = useState(null);
  const [order, setOrder] = useState(null);
  const [donation, setDonation] = useState(null);
  const [donationPaymentUrl, setDonationPaymentUrl] = useState(null);
  const [donationBankAccounts, setDonationBankAccounts] = useState([]);
  const [ppobOrder, setPpobOrder] = useState(null);
  const [ppobPaymentUrl, setPpobPaymentUrl] = useState(null);
  const [ppobBankAccounts, setPpobBankAccounts] = useState([]);
  const [webinarData, setWebinarData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Payment proof form
  const [proofForm, setProofForm] = useState({ bank_name: '', account_name: '', amount: '', transfer_date: '', proof_url: '', notes: '' });
  const [proofSubmitting, setProofSubmitting] = useState(false);
  const [proofSubmitted, setProofSubmitted] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const storeRes = await api.get(`/store/${subdomain}`);
        setStore(storeRes.data);

        if (type === 'ppob' && ppobOrderId) {
          const pRes = await api.get(`/store/${subdomain}/thank-you/ppob/${ppobOrderId}`);
          setPpobOrder(pRes.data.order || pRes.data);
          setPpobPaymentUrl(pRes.data.payment_url || null);
          setPpobBankAccounts(pRes.data.bank_accounts || []);
        } else if (type === 'donation' && donationId) {
          const dRes = await api.get(`/store/${subdomain}/thank-you/donation/${donationId}`);
          setDonation(dRes.data.donation || dRes.data);
          setDonationPaymentUrl(dRes.data.payment_url || null);
          setDonationBankAccounts(dRes.data.bank_accounts || []);
        } else if (type === 'webinar' && orderNumber) {
          const wRes = await api.get(`/store/${subdomain}/thank-you/webinar/${orderNumber}`);
          setWebinarData(wRes.data);
        } else if (orderNumber) {
          const oRes = await api.get(`/store/${subdomain}/thank-you/order/${orderNumber}`);
          setOrder(oRes.data);
        }
      } catch (e) {
        setError('Data tidak ditemukan');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [subdomain, orderNumber, donationId, ppobOrderId, type]);

  const fmt = (v) => new Intl.NumberFormat('id-ID').format(v || 0);
  const pc = store?.primary_color || '#2D6CDF';

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 flex items-center justify-center">
      <div className="text-center">
        <Loader2 size={40} className="animate-spin text-emerald-500 mx-auto mb-4" />
        <p className="text-slate-500">Memuat detail pesanan...</p>
      </div>
    </div>
  );

  if (error || (!order && !donation && !ppobOrder && !webinarData)) return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
      <Card className="max-w-md w-full mx-4 border-0 shadow-xl">
        <CardContent className="p-8 text-center">
          <AlertCircle size={48} className="mx-auto text-slate-300 mb-4" />
          <h2 className="text-xl font-bold text-slate-900 mb-2">Pesanan Tidak Ditemukan</h2>
          <p className="text-slate-500 mb-6">Halaman ini mungkin sudah kedaluwarsa atau link tidak valid.</p>
          <Link to={`/store/${subdomain}`}>
            <Button style={{ backgroundColor: pc }}>
              <ArrowLeft size={16} className="mr-2" /> Kembali ke Toko
            </Button>
          </Link>
        </CardContent>
      </Card>
    </div>
  );

  // ===================== WEBINAR THANK YOU =====================
  if (type === 'webinar' && webinarData) {
    const wo = webinarData.order || {};
    const wb = webinarData.webinar || {};
    const wReg = webinarData.registration || {};
    const wPaymentUrl = webinarData.payment_url || null;
    const wBankAccounts = webinarData.bank_accounts || [];
    const wIsPaid = wo.payment_status === 'paid';
    const wIsFree = wo.total <= 0 || wReg.payment_status === 'free';
    const payMethodLabel = { qris: 'QRIS', va: 'Virtual Account', ewallet: 'E-Wallet', bank_transfer: 'Transfer Bank' }[wo.payment_method] || wo.payment_method || '-';

    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-50 via-purple-50 to-fuchsia-50">
        {/* Floating particles */}
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="absolute animate-bounce" style={{
              left: `${12 + i * 14}%`, top: `${8 + (i % 3) * 25}%`,
              animationDelay: `${i * 0.3}s`, animationDuration: `${2 + i * 0.4}s`
            }}>
              <Star size={14 + i * 3} className="text-violet-200/50" />
            </div>
          ))}
        </div>

        <header className="bg-white/80 backdrop-blur-sm border-b border-violet-100 sticky top-0 z-50">
          <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
            <Link to={`/store/${subdomain}`} className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors">
              <ArrowLeft size={20} />
              <span className="font-medium">{store?.name}</span>
            </Link>
            <Badge variant="outline" className="text-violet-600 border-violet-200 gap-1.5">
              <Video size={14} /> Webinar
            </Badge>
          </div>
        </header>

        <main className="max-w-3xl mx-auto px-4 py-8 relative z-10">
          <div className="space-y-6">
            {/* Hero Banner */}
            <Card className="border-0 shadow-2xl overflow-hidden rounded-3xl">
              <div className="bg-gradient-to-br from-violet-500 via-purple-500 to-fuchsia-500 p-8 text-center text-white relative overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute top-4 left-8 w-32 h-32 rounded-full border-4 border-white" />
                  <div className="absolute bottom-4 right-12 w-24 h-24 rounded-full border-4 border-white" />
                </div>
                <div className="relative z-10">
                  <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center mx-auto mb-4">
                    {wIsFree || wIsPaid ? (
                      <CheckCircle size={40} className="text-white" />
                    ) : (
                      <Clock size={40} className="text-white" />
                    )}
                  </div>
                  <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                    {wIsFree || wIsPaid ? 'Pendaftaran Berhasil!' : 'Menunggu Pembayaran'}
                  </h1>
                  <p className="text-white/90 text-lg">
                    {wIsFree || wIsPaid
                      ? `Anda terdaftar di webinar "${wb.title || 'Webinar'}"`
                      : 'Selesaikan pembayaran untuk mengkonfirmasi pendaftaran'}
                  </p>
                  <div className="mt-4 inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-full px-5 py-2 text-sm">
                    <Video size={16} /> {wo.order_number}
                  </div>
                </div>
              </div>

              <CardContent className="p-0">
                <div className={`grid ${wIsFree ? 'grid-cols-2' : 'grid-cols-3'} divide-x divide-slate-100`}>
                  {!wIsFree && (
                    <div className="p-4 text-center">
                      <p className="text-xs text-slate-500 mb-1">Total</p>
                      <p className="text-lg font-bold" style={{ color: pc }}>Rp {fmt(wo.total)}</p>
                    </div>
                  )}
                  <div className="p-4 text-center">
                    <p className="text-xs text-slate-500 mb-1">{wIsFree ? 'Harga' : 'Pembayaran'}</p>
                    <p className="text-sm font-semibold text-slate-700">{wIsFree ? 'Gratis' : payMethodLabel}</p>
                  </div>
                  <div className="p-4 text-center">
                    <p className="text-xs text-slate-500 mb-1">Status</p>
                    <Badge className={wIsFree || wIsPaid ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                      {wIsFree || wIsPaid ? 'Terdaftar' : 'Menunggu'}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Payment Instructions (paid + not yet paid) */}
            {!wIsFree && !wIsPaid && (
              <Card className="border-0 shadow-xl rounded-2xl overflow-hidden">
                <div className="h-1.5 bg-gradient-to-r from-violet-500 via-purple-500 to-fuchsia-500" />
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <CreditCard size={20} style={{ color: pc }} /> Instruksi Pembayaran
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {webinarData.payment_provider === 'bank_transfer' || wo.payment_method === 'bank_transfer' ? (
                    <div className="space-y-4">
                      <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5">
                        <div className="flex items-center gap-2 text-blue-800 mb-4">
                          <CreditCard size={18} />
                          <p className="font-semibold">Transfer ke Rekening</p>
                        </div>
                        {wBankAccounts.length > 0 ? (
                          <div className="space-y-3">
                            {wBankAccounts.map((acc, i) => (
                              <div key={i} className="bg-white rounded-xl p-4 border border-blue-100 hover:shadow-md transition-shadow">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="font-bold text-blue-900 text-lg">{acc.bank_name}</span>
                                  <button onClick={() => { navigator.clipboard.writeText(acc.account_number); toast.success('Nomor rekening disalin'); }} className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 px-2.5 py-1 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
                                    <Copy size={12} /> Salin
                                  </button>
                                </div>
                                <p className="font-mono text-xl tracking-wider font-bold text-slate-900">{acc.account_number}</p>
                                <p className="text-sm text-slate-500 mt-1">a.n. {acc.account_holder || acc.account_name}</p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-blue-600">Hubungi penjual untuk informasi rekening transfer.</p>
                        )}
                        <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center gap-2 text-sm text-amber-700">
                          <AlertCircle size={16} className="flex-shrink-0" />
                          <span>Transfer tepat <strong>Rp {fmt(wo.total)}</strong> agar verifikasi lebih cepat</span>
                        </div>
                      </div>

                      {/* Payment Proof Form */}
                      {!proofSubmitted ? (
                        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 space-y-4">
                          <div className="flex items-center gap-2 text-slate-700">
                            <Upload size={18} />
                            <p className="font-semibold">Konfirmasi Pembayaran</p>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div><Label className="text-xs text-slate-500">Bank Pengirim</Label><Input placeholder="BCA, BNI, BRI..." value={proofForm.bank_name} onChange={e => setProofForm({...proofForm, bank_name: e.target.value})} className="mt-1" /></div>
                            <div><Label className="text-xs text-slate-500">Nama Pengirim</Label><Input placeholder="Nama di rekening" value={proofForm.account_name} onChange={e => setProofForm({...proofForm, account_name: e.target.value})} className="mt-1" /></div>
                            <div><Label className="text-xs text-slate-500">Jumlah Transfer</Label><Input type="number" placeholder={String(wo.total)} value={proofForm.amount} onChange={e => setProofForm({...proofForm, amount: e.target.value})} className="mt-1" /></div>
                            <div><Label className="text-xs text-slate-500">Tanggal Transfer</Label><Input type="date" value={proofForm.transfer_date} onChange={e => setProofForm({...proofForm, transfer_date: e.target.value})} className="mt-1" /></div>
                          </div>
                          <div><Label className="text-xs text-slate-500">URL Bukti Transfer (opsional)</Label><Input placeholder="https://... link gambar bukti" value={proofForm.proof_url} onChange={e => setProofForm({...proofForm, proof_url: e.target.value})} className="mt-1" /></div>
                          <Button
                            className="w-full h-11 rounded-xl font-semibold"
                            style={{ backgroundColor: pc }}
                            disabled={proofSubmitting || !proofForm.bank_name || !proofForm.account_name || !proofForm.amount}
                            onClick={async () => {
                              setProofSubmitting(true);
                              try {
                                await api.post(`/store/${subdomain}/payment-confirmation`, {
                                  order_number: wo.order_number, customer_email: wo.customer_email,
                                  bank_name: proofForm.bank_name, account_name: proofForm.account_name,
                                  amount: parseInt(proofForm.amount), transfer_date: proofForm.transfer_date,
                                  proof_url: proofForm.proof_url, notes: proofForm.notes || ''
                                });
                                setProofSubmitted(true);
                                toast.success('Konfirmasi pembayaran terkirim!');
                              } catch (e) { toast.error(e.response?.data?.detail || 'Gagal mengirim konfirmasi'); }
                              finally { setProofSubmitting(false); }
                            }}
                          >
                            {proofSubmitting ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Upload size={16} className="mr-2" />}
                            Kirim Konfirmasi
                          </Button>
                        </div>
                      ) : (
                        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 text-center">
                          <CheckCircle size={40} className="text-emerald-500 mx-auto mb-3" />
                          <p className="font-semibold text-emerald-800 text-lg">Konfirmasi Terkirim!</p>
                          <p className="text-sm text-emerald-600 mt-1">Penjual akan memverifikasi pembayaran Anda</p>
                        </div>
                      )}
                    </div>
                  ) : wPaymentUrl ? (
                    <div className="space-y-4">
                      <div className="bg-gradient-to-br from-violet-50 to-purple-50 border border-violet-200 rounded-2xl p-5 space-y-4">
                        <div className="flex items-center gap-2 text-violet-800">
                          <CreditCard size={18} />
                          <p className="font-semibold">Selesaikan Pembayaran</p>
                        </div>
                        <p className="text-sm text-slate-600">Metode: <strong>{payMethodLabel}</strong></p>
                        <a href={wPaymentUrl} target="_blank" rel="noreferrer" className="block">
                          <Button className="w-full h-12 text-base font-bold rounded-xl shadow-lg" style={{ backgroundColor: pc }}>
                            <CreditCard size={18} className="mr-2" /> Bayar Sekarang — Rp {fmt(wo.total)}
                          </Button>
                        </a>
                        <div className="flex items-center gap-4 justify-center text-xs text-violet-600">
                          <span className="flex items-center gap-1"><Clock size={12} /> 24 jam untuk bayar</span>
                          <span className="flex items-center gap-1"><Shield size={12} /> Aman & terenkripsi</span>
                        </div>
                      </div>
                      <div className="bg-slate-50 rounded-2xl p-5 text-sm text-slate-600 space-y-2">
                        <p className="font-semibold text-slate-700 flex items-center gap-2"><ChevronRight size={16} style={{ color: pc }} /> Langkah Pembayaran</p>
                        <ol className="list-decimal list-inside space-y-1.5 ml-1">
                          <li>Klik tombol <strong>"Bayar Sekarang"</strong> di atas</li>
                          <li>Ikuti instruksi pembayaran {payMethodLabel}</li>
                          <li>Pendaftaran Anda akan otomatis dikonfirmasi setelah pembayaran</li>
                          <li>Anda akan menerima email konfirmasi & link meeting</li>
                        </ol>
                      </div>
                    </div>
                  ) : null}
                </CardContent>
              </Card>
            )}

            {/* Webinar Detail */}
            <Card className="border-0 shadow-xl rounded-2xl overflow-hidden">
              <div className="h-1 bg-gradient-to-r from-violet-400 to-purple-400" />
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Video size={20} style={{ color: pc }} /> Detail Webinar
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                {wb.thumbnail && (
                  <div className="rounded-xl overflow-hidden">
                    <img src={wb.thumbnail} alt={wb.title} className="w-full h-48 object-cover" />
                  </div>
                )}
                <div>
                  <h3 className="text-xl font-bold text-slate-900">{wb.title}</h3>
                  {wb.description && <p className="text-sm text-slate-500 mt-2 line-clamp-3">{wb.description}</p>}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-50 rounded-xl p-3">
                    <p className="text-xs text-slate-500 mb-1">Pembicara</p>
                    <p className="font-medium text-sm text-slate-900 flex items-center gap-1.5"><User size={14} /> {wb.speaker_name || '-'}</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-3">
                    <p className="text-xs text-slate-500 mb-1">Jadwal</p>
                    <p className="font-medium text-sm text-slate-900 flex items-center gap-1.5">
                      <Calendar size={14} />
                      {wb.scheduled_at ? new Date(wb.scheduled_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-3">
                    <p className="text-xs text-slate-500 mb-1">Nomor Pesanan</p>
                    <p className="font-mono font-bold text-sm" style={{ color: pc }}>{wo.order_number}</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-3">
                    <p className="text-xs text-slate-500 mb-1">Status Pendaftaran</p>
                    <Badge className={wIsFree || wIsPaid ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                      {wIsFree || wIsPaid ? 'Terdaftar' : 'Menunggu Pembayaran'}
                    </Badge>
                  </div>
                </div>

                {/* Meeting URL (shown only if paid/free and has meeting URL) */}
                {(wIsFree || wIsPaid) && wb.meeting_url && (
                  <div className="bg-violet-50 border border-violet-200 rounded-xl p-4">
                    <p className="text-sm font-semibold text-violet-800 mb-2 flex items-center gap-2"><Video size={16} /> Link Meeting</p>
                    <a href={wb.meeting_url} target="_blank" rel="noreferrer" className="text-sm text-violet-600 underline break-all">{wb.meeting_url}</a>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Customer Info */}
            <Card className="border-0 shadow-xl rounded-2xl overflow-hidden">
              <div className="h-1 bg-gradient-to-r from-purple-400 to-pink-400" />
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Mail size={20} className="text-purple-500" /> Informasi Peserta
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                    <div className="w-9 h-9 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <Star size={16} className="text-purple-500" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Nama</p>
                      <p className="font-medium text-sm text-slate-900">{wo.customer_name || wReg.participant_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                    <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <Mail size={16} className="text-blue-500" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Email</p>
                      <p className="font-medium text-sm text-slate-900">{wo.customer_email || wReg.participant_email}</p>
                    </div>
                  </div>
                  {(wo.customer_phone || wReg.participant_phone) && (
                    <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                      <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                        <Phone size={16} className="text-emerald-500" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Telepon</p>
                        <p className="font-medium text-sm text-slate-900">{wo.customer_phone || wReg.participant_phone}</p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* What's Next */}
            <Card className="border-0 shadow-xl rounded-2xl overflow-hidden">
              <div className="h-1 bg-gradient-to-r from-amber-400 to-orange-400" />
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Clock size={20} className="text-amber-500" /> Selanjutnya
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[
                    { step: 1, title: wIsFree || wIsPaid ? 'Pendaftaran Dikonfirmasi' : 'Selesaikan Pembayaran', desc: wIsFree || wIsPaid ? 'Anda sudah terdaftar di webinar ini' : 'Lakukan pembayaran sesuai instruksi', done: wIsFree || wIsPaid, icon: CreditCard, color: 'emerald' },
                    { step: 2, title: 'Terima Email Konfirmasi', desc: 'Anda akan menerima email berisi detail & link meeting', done: false, icon: Mail, color: 'blue' },
                    { step: 3, title: 'Ikuti Webinar', desc: wb.scheduled_at ? `Jadwal: ${new Date(wb.scheduled_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}` : 'Nantikan jadwal dari penyelenggara', done: false, icon: Video, color: 'purple' },
                  ].map(({ step, title, desc, done, icon: Icon, color }) => (
                    <div key={step} className="flex items-start gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${done ? `bg-${color}-100` : 'bg-slate-100'}`}>
                        {done ? <CheckCircle size={20} className={`text-${color}-500`} /> : <Icon size={20} className="text-slate-400" />}
                      </div>
                      <div className="flex-1 pb-4 border-b border-slate-50 last:border-0">
                        <p className={`font-medium ${done ? 'text-slate-900' : 'text-slate-500'}`}>{title}</p>
                        <p className="text-sm text-slate-400">{desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center pb-8">
              {wPaymentUrl && !wIsPaid && !wIsFree && (
                <a href={wPaymentUrl} target="_blank" rel="noreferrer">
                  <Button className="w-full sm:w-auto h-12 text-base font-semibold rounded-xl shadow-lg" style={{ backgroundColor: pc }}>
                    <CreditCard size={18} className="mr-2" /> Bayar Sekarang
                  </Button>
                </a>
              )}
              <Link to={`/store/${subdomain}`}>
                <Button variant="outline" size="lg" className="w-full sm:w-auto rounded-xl border-violet-200 hover:bg-violet-50 h-12">
                  <ShoppingBag size={18} className="mr-2" /> Kembali ke Toko
                </Button>
              </Link>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // ===================== PPOB THANK YOU =====================
  if (type === 'ppob' && ppobOrder) {
    const po = ppobOrder;
    const ppobIsPaid = po.payment_status === 'paid' || po.status === 'success';
    const payMethodLabel = { qris: 'QRIS', va: 'Virtual Account', ewallet: 'E-Wallet', bank_transfer: 'Transfer Bank' }[po.payment_method] || po.payment_method || '-';

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="absolute animate-bounce" style={{
              left: `${12 + i * 14}%`, top: `${8 + (i % 3) * 25}%`,
              animationDelay: `${i * 0.3}s`, animationDuration: `${2 + i * 0.4}s`
            }}>
              <Star size={14 + i * 3} className="text-indigo-200/50" />
            </div>
          ))}
        </div>

        <header className="bg-white/80 backdrop-blur-sm border-b border-indigo-100 sticky top-0 z-50">
          <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
            <Link to={`/store/${subdomain}`} className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors">
              <ArrowLeft size={20} />
              <span className="font-medium">{store?.name}</span>
            </Link>
            <Badge variant="outline" className="text-indigo-600 border-indigo-200 gap-1.5">
              <FileText size={14} /> #{po.order_number}
            </Badge>
          </div>
        </header>

        <main className="max-w-3xl mx-auto px-4 py-8 relative z-10">
          <div className="space-y-6">
            {/* Hero Banner */}
            <Card className="border-0 shadow-2xl overflow-hidden rounded-3xl">
              <div className="bg-gradient-to-br from-indigo-500 via-blue-500 to-cyan-500 p-8 text-center text-white relative overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute top-4 left-8 w-32 h-32 rounded-full border-4 border-white" />
                  <div className="absolute bottom-4 right-12 w-24 h-24 rounded-full border-4 border-white" />
                </div>
                <div className="relative z-10">
                  <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center mx-auto mb-4">
                    <CheckCircle size={40} className="text-white" />
                  </div>
                  <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Plus Jakarta Sans' }}>Pesanan PPOB Dibuat!</h1>
                  <p className="text-white/90 text-lg">Silakan selesaikan pembayaran</p>
                  <div className="mt-4 inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-full px-5 py-2 text-sm">
                    <FileText size={16} /> {po.order_number}
                  </div>
                </div>
              </div>

              <CardContent className="p-0">
                <div className="grid grid-cols-3 divide-x divide-slate-100">
                  <div className="p-4 text-center">
                    <p className="text-xs text-slate-500 mb-1">Total</p>
                    <p className="text-lg font-bold" style={{ color: pc }}>Rp {fmt(po.selling_price)}</p>
                  </div>
                  <div className="p-4 text-center">
                    <p className="text-xs text-slate-500 mb-1">Pembayaran</p>
                    <p className="text-sm font-semibold text-slate-700">{payMethodLabel}</p>
                  </div>
                  <div className="p-4 text-center">
                    <p className="text-xs text-slate-500 mb-1">Status</p>
                    <Badge className={ppobIsPaid ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                      {ppobIsPaid ? 'Dibayar' : 'Menunggu'}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Payment Instructions */}
            {!ppobIsPaid && (
              <Card className="border-0 shadow-xl rounded-2xl overflow-hidden">
                <div className="h-1.5 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500" />
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <CreditCard size={20} style={{ color: pc }} /> Instruksi Pembayaran
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {po.payment_provider === 'bank_transfer' ? (
                    <div className="space-y-4">
                      <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5">
                        <div className="flex items-center gap-2 text-blue-800 mb-4">
                          <CreditCard size={18} />
                          <p className="font-semibold">Transfer ke Rekening</p>
                        </div>
                        {ppobBankAccounts.length > 0 ? (
                          <div className="space-y-3">
                            {ppobBankAccounts.map((acc, i) => (
                              <div key={i} className="bg-white rounded-xl p-4 border border-blue-100 hover:shadow-md transition-shadow">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="font-bold text-blue-900 text-lg">{acc.bank_name}</span>
                                  <button onClick={() => { navigator.clipboard.writeText(acc.account_number); toast.success('Nomor rekening disalin'); }} className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 px-2.5 py-1 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
                                    <Copy size={12} /> Salin
                                  </button>
                                </div>
                                <p className="font-mono text-xl tracking-wider font-bold text-slate-900">{acc.account_number}</p>
                                <p className="text-sm text-slate-500 mt-1">a.n. {acc.account_holder || acc.account_name}</p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-blue-600">Hubungi penjual untuk informasi rekening transfer.</p>
                        )}
                        <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center gap-2 text-sm text-amber-700">
                          <AlertCircle size={16} className="flex-shrink-0" />
                          <span>Transfer tepat <strong>Rp {fmt(po.selling_price)}</strong> agar verifikasi lebih cepat</span>
                        </div>
                      </div>

                      {/* Payment Proof Form */}
                      {!proofSubmitted ? (
                        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 space-y-4">
                          <div className="flex items-center gap-2 text-slate-700">
                            <Upload size={18} />
                            <p className="font-semibold">Konfirmasi Pembayaran</p>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div><Label className="text-xs text-slate-500">Bank Pengirim</Label><Input placeholder="BCA, BNI, BRI..." value={proofForm.bank_name} onChange={e => setProofForm({...proofForm, bank_name: e.target.value})} className="mt-1" /></div>
                            <div><Label className="text-xs text-slate-500">Nama Pengirim</Label><Input placeholder="Nama di rekening" value={proofForm.account_name} onChange={e => setProofForm({...proofForm, account_name: e.target.value})} className="mt-1" /></div>
                            <div><Label className="text-xs text-slate-500">Jumlah Transfer</Label><Input type="number" placeholder={String(po.selling_price)} value={proofForm.amount} onChange={e => setProofForm({...proofForm, amount: e.target.value})} className="mt-1" /></div>
                            <div><Label className="text-xs text-slate-500">Tanggal Transfer</Label><Input type="date" value={proofForm.transfer_date} onChange={e => setProofForm({...proofForm, transfer_date: e.target.value})} className="mt-1" /></div>
                          </div>
                          <div><Label className="text-xs text-slate-500">URL Bukti Transfer (opsional)</Label><Input placeholder="https://... link gambar bukti" value={proofForm.proof_url} onChange={e => setProofForm({...proofForm, proof_url: e.target.value})} className="mt-1" /></div>
                          <Button
                            className="w-full h-11 rounded-xl font-semibold"
                            style={{ backgroundColor: pc }}
                            disabled={proofSubmitting || !proofForm.bank_name || !proofForm.account_name || !proofForm.amount}
                            onClick={async () => {
                              setProofSubmitting(true);
                              try {
                                await api.post(`/store/${subdomain}/payment-confirmation`, {
                                  order_number: po.order_number, customer_email: po.customer_email,
                                  bank_name: proofForm.bank_name, account_name: proofForm.account_name,
                                  amount: parseInt(proofForm.amount), transfer_date: proofForm.transfer_date,
                                  proof_url: proofForm.proof_url, notes: proofForm.notes || ''
                                });
                                setProofSubmitted(true);
                                toast.success('Konfirmasi pembayaran terkirim!');
                              } catch (e) { toast.error(e.response?.data?.detail || 'Gagal mengirim konfirmasi'); }
                              finally { setProofSubmitting(false); }
                            }}
                          >
                            {proofSubmitting ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Upload size={16} className="mr-2" />}
                            Kirim Konfirmasi
                          </Button>
                        </div>
                      ) : (
                        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 text-center">
                          <CheckCircle size={40} className="text-emerald-500 mx-auto mb-3" />
                          <p className="font-semibold text-emerald-800 text-lg">Konfirmasi Terkirim!</p>
                          <p className="text-sm text-emerald-600 mt-1">Penjual akan memverifikasi pembayaran Anda</p>
                        </div>
                      )}
                    </div>
                  ) : ppobPaymentUrl ? (
                    <div className="space-y-4">
                      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-5 space-y-4">
                        <div className="flex items-center gap-2 text-blue-800">
                          <CreditCard size={18} />
                          <p className="font-semibold">Selesaikan Pembayaran</p>
                        </div>
                        <p className="text-sm text-slate-600">
                          Metode: <strong>{payMethodLabel}</strong>
                        </p>
                        <a href={ppobPaymentUrl} target="_blank" rel="noreferrer" className="block">
                          <Button className="w-full h-12 text-base font-bold rounded-xl shadow-lg" style={{ backgroundColor: pc }}>
                            <CreditCard size={18} className="mr-2" /> Bayar Sekarang — Rp {fmt(po.selling_price)}
                          </Button>
                        </a>
                        <div className="flex items-center gap-4 justify-center text-xs text-blue-600">
                          <span className="flex items-center gap-1"><Clock size={12} /> 24 jam untuk bayar</span>
                          <span className="flex items-center gap-1"><Shield size={12} /> Aman & terenkripsi</span>
                        </div>
                      </div>
                      <div className="bg-slate-50 rounded-2xl p-5 text-sm text-slate-600 space-y-2">
                        <p className="font-semibold text-slate-700 flex items-center gap-2"><ChevronRight size={16} style={{ color: pc }} /> Langkah Pembayaran</p>
                        <ol className="list-decimal list-inside space-y-1.5 ml-1">
                          <li>Klik tombol <strong>"Bayar Sekarang"</strong> di atas</li>
                          <li>Ikuti instruksi pembayaran {payMethodLabel}</li>
                          <li>Pesanan PPOB akan otomatis diproses setelah pembayaran</li>
                          <li>Anda akan menerima notifikasi konfirmasi</li>
                        </ol>
                      </div>
                    </div>
                  ) : null}
                </CardContent>
              </Card>
            )}

            {/* PPOB Order Detail */}
            <Card className="border-0 shadow-xl rounded-2xl overflow-hidden">
              <div className="h-1 bg-gradient-to-r from-indigo-400 to-blue-400" />
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Package size={20} style={{ color: pc }} /> Detail Pesanan PPOB
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-50 rounded-xl p-3">
                    <p className="text-xs text-slate-500 mb-1">Nomor Pesanan</p>
                    <p className="font-mono font-bold text-sm" style={{ color: pc }}>{po.order_number}</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-3">
                    <p className="text-xs text-slate-500 mb-1">Tanggal</p>
                    <p className="font-medium text-sm">{new Date(po.created_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-3">
                    <p className="text-xs text-slate-500 mb-1">Status</p>
                    <Badge className={po.status === 'success' ? 'bg-emerald-100 text-emerald-700' : po.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>
                      {po.status === 'success' ? 'Berhasil' : po.status === 'failed' ? 'Gagal' : 'Menunggu Pembayaran'}
                    </Badge>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-3">
                    <p className="text-xs text-slate-500 mb-1">Pembayaran</p>
                    <p className="font-medium text-sm">{payMethodLabel}</p>
                  </div>
                </div>

                <div className="border-t border-slate-100 pt-4">
                  <p className="text-sm font-semibold text-slate-700 mb-3">Produk</p>
                  <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-500">Produk</span>
                      <span className="text-sm font-medium text-slate-900">{po.product_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-500">SKU</span>
                      <span className="text-sm font-mono text-slate-700">{po.sku}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-500">Nomor Tujuan</span>
                      <span className="text-sm font-medium text-slate-900">{po.customer_number}</span>
                    </div>
                    {po.sn && (
                      <div className="flex justify-between">
                        <span className="text-sm text-slate-500">Serial Number</span>
                        <span className="text-sm font-mono font-bold text-emerald-600">{po.sn}</span>
                      </div>
                    )}
                    <div className="border-t border-slate-200 pt-3 flex justify-between">
                      <span className="font-bold text-base text-slate-900">Total Bayar</span>
                      <span className="text-xl font-bold" style={{ color: pc }}>Rp {fmt(po.selling_price)}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Customer Info */}
            <Card className="border-0 shadow-xl rounded-2xl overflow-hidden">
              <div className="h-1 bg-gradient-to-r from-purple-400 to-pink-400" />
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Mail size={20} className="text-purple-500" /> Informasi Pembeli
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                    <div className="w-9 h-9 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <Star size={16} className="text-purple-500" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Nama</p>
                      <p className="font-medium text-sm text-slate-900">{po.customer_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                    <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <Mail size={16} className="text-blue-500" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Email</p>
                      <p className="font-medium text-sm text-slate-900">{po.customer_email}</p>
                    </div>
                  </div>
                  {po.customer_phone && (
                    <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                      <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                        <Phone size={16} className="text-emerald-500" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Telepon</p>
                        <p className="font-medium text-sm text-slate-900">{po.customer_phone}</p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* What's Next */}
            <Card className="border-0 shadow-xl rounded-2xl overflow-hidden">
              <div className="h-1 bg-gradient-to-r from-amber-400 to-orange-400" />
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Clock size={20} className="text-amber-500" /> Selanjutnya
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[
                    { step: 1, title: ppobIsPaid ? 'Pembayaran Diterima' : 'Selesaikan Pembayaran', desc: ppobIsPaid ? 'Pembayaran sudah dikonfirmasi' : 'Lakukan pembayaran sesuai instruksi', done: ppobIsPaid, icon: CreditCard, color: 'emerald' },
                    { step: 2, title: 'PPOB Diproses', desc: 'Penjual akan memproses transaksi PPOB Anda', done: po.status === 'success', icon: Package, color: 'blue' },
                    { step: 3, title: 'Transaksi Selesai', desc: 'Anda akan menerima konfirmasi & serial number', done: po.status === 'success', icon: Gift, color: 'purple' },
                  ].map(({ step, title, desc, done, icon: Icon, color }) => (
                    <div key={step} className="flex items-start gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${done ? `bg-${color}-100` : 'bg-slate-100'}`}>
                        {done ? <CheckCircle size={20} className={`text-${color}-500`} /> : <Icon size={20} className="text-slate-400" />}
                      </div>
                      <div className="flex-1 pb-4 border-b border-slate-50 last:border-0">
                        <p className={`font-medium ${done ? 'text-slate-900' : 'text-slate-500'}`}>{title}</p>
                        <p className="text-sm text-slate-400">{desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center pb-8">
              {ppobPaymentUrl && !ppobIsPaid && (
                <a href={ppobPaymentUrl} target="_blank" rel="noreferrer">
                  <Button className="w-full sm:w-auto h-12 text-base font-semibold rounded-xl shadow-lg" style={{ backgroundColor: pc }}>
                    <CreditCard size={18} className="mr-2" /> Bayar Sekarang
                  </Button>
                </a>
              )}
              <Link to={`/store/${subdomain}`}>
                <Button variant="outline" size="lg" className="w-full sm:w-auto rounded-xl border-indigo-200 hover:bg-indigo-50 h-12">
                  <ShoppingBag size={18} className="mr-2" /> Kembali ke Toko
                </Button>
              </Link>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // ===================== DONATION THANK YOU =====================
  if (type === 'donation' && donation) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-rose-50 to-orange-50">
        {/* Floating particles */}
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="absolute animate-bounce" style={{
              left: `${15 + i * 15}%`, top: `${10 + (i % 3) * 25}%`,
              animationDelay: `${i * 0.3}s`, animationDuration: `${2 + i * 0.5}s`
            }}>
              <Heart size={16 + i * 4} className="text-pink-200/60" />
            </div>
          ))}
        </div>

        <header className="bg-white/80 backdrop-blur-sm border-b border-pink-100 sticky top-0 z-50">
          <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
            <Link to={`/store/${subdomain}`} className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors">
              <ArrowLeft size={20} />
              <span className="font-medium">{store?.name}</span>
            </Link>
          </div>
        </header>

        <main className="max-w-3xl mx-auto px-4 py-8 relative z-10">
          <div className="space-y-6">
            {/* Hero Success */}
            <Card className="border-0 shadow-2xl overflow-hidden rounded-3xl">
              <div className="bg-gradient-to-br from-pink-500 via-rose-500 to-orange-400 p-8 text-center text-white relative overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                  {[...Array(20)].map((_, i) => (
                    <Heart key={i} size={20 + Math.random() * 30} className="absolute" style={{
                      left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`,
                      transform: `rotate(${Math.random() * 360}deg)`
                    }} />
                  ))}
                </div>
                <div className="relative z-10">
                  <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center mx-auto mb-4 animate-pulse">
                    <Heart size={40} className="text-white fill-white" />
                  </div>
                  <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Plus Jakarta Sans' }}>Terima Kasih!</h1>
                  <p className="text-white/90 text-lg">Donasi Anda sangat berarti</p>
                </div>
              </div>

              <CardContent className="p-6 space-y-5">
                {/* Donation Info */}
                <div className="bg-pink-50 rounded-2xl p-5">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm text-slate-500">Nominal Donasi</span>
                    <span className="text-2xl font-bold text-pink-600">Rp {fmt(donation.amount)}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-slate-500">Donatur</p>
                      <p className="font-medium text-slate-900">{donation.is_anonymous ? 'Hamba Allah' : donation.donor_name}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Program</p>
                      <p className="font-medium text-slate-900">{donation.campaign_title || donation.product_name || 'Donasi'}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Status</p>
                      <Badge className={donation.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                        {donation.status === 'completed' ? 'Berhasil' : 'Menunggu Pembayaran'}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-slate-500">Tanggal</p>
                      <p className="font-medium text-slate-900">{new Date(donation.created_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                    </div>
                  </div>
                  {donation.message && (
                    <div className="mt-4 pt-4 border-t border-pink-100">
                      <p className="text-sm text-slate-500 mb-1">Pesan / Doa</p>
                      <p className="text-slate-700 italic">"{donation.message}"</p>
                    </div>
                  )}
                </div>

                {/* Payment */}
                {donation.status !== 'completed' && (
                  <>
                    {/* Bank Transfer: show bank account info */}
                    {donation.payment_method === 'bank_transfer' ? (
                      <div className="space-y-4">
                        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5">
                          <div className="flex items-center gap-2 text-blue-800 mb-4">
                            <CreditCard size={18} />
                            <p className="font-semibold">Transfer ke Rekening</p>
                          </div>
                          {donationBankAccounts.length > 0 ? (
                            <div className="space-y-3">
                              {donationBankAccounts.map((acc, i) => (
                                <div key={i} className="bg-white rounded-xl p-4 border border-blue-100 hover:shadow-md transition-shadow">
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="font-bold text-blue-900 text-lg">{acc.bank_name}</span>
                                    <button onClick={() => { navigator.clipboard.writeText(acc.account_number); toast.success('Nomor rekening disalin'); }} className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 px-2.5 py-1 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
                                      <Copy size={12} /> Salin
                                    </button>
                                  </div>
                                  <p className="font-mono text-xl tracking-wider font-bold text-slate-900">{acc.account_number}</p>
                                  <p className="text-sm text-slate-500 mt-1">a.n. {acc.account_holder || acc.account_name}</p>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-blue-600">Hubungi penjual untuk informasi rekening transfer.</p>
                          )}
                          <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center gap-2 text-sm text-amber-700">
                            <AlertCircle size={16} className="flex-shrink-0" />
                            <span>Transfer tepat <strong>Rp {fmt(donation.amount)}</strong> agar verifikasi lebih cepat</span>
                          </div>
                        </div>

                        {/* Payment Proof Form for bank transfers */}
                        {!proofSubmitted ? (
                          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 space-y-4">
                            <div className="flex items-center gap-2 text-slate-700">
                              <Upload size={18} />
                              <p className="font-semibold">Konfirmasi Pembayaran</p>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              <div><Label className="text-xs text-slate-500">Bank Pengirim</Label><Input placeholder="BCA, BNI, BRI..." value={proofForm.bank_name} onChange={e => setProofForm({...proofForm, bank_name: e.target.value})} className="mt-1" /></div>
                              <div><Label className="text-xs text-slate-500">Nama Pengirim</Label><Input placeholder="Nama di rekening" value={proofForm.account_name} onChange={e => setProofForm({...proofForm, account_name: e.target.value})} className="mt-1" /></div>
                              <div><Label className="text-xs text-slate-500">Jumlah Transfer</Label><Input type="number" placeholder={String(donation.amount)} value={proofForm.amount} onChange={e => setProofForm({...proofForm, amount: e.target.value})} className="mt-1" /></div>
                              <div><Label className="text-xs text-slate-500">Tanggal Transfer</Label><Input type="date" value={proofForm.transfer_date} onChange={e => setProofForm({...proofForm, transfer_date: e.target.value})} className="mt-1" /></div>
                            </div>
                            <div><Label className="text-xs text-slate-500">URL Bukti Transfer (opsional)</Label><Input placeholder="https://... link gambar bukti" value={proofForm.proof_url} onChange={e => setProofForm({...proofForm, proof_url: e.target.value})} className="mt-1" /></div>
                            <Button
                              className="w-full h-11 rounded-xl font-semibold"
                              style={{ backgroundColor: pc }}
                              disabled={proofSubmitting || !proofForm.bank_name || !proofForm.account_name || !proofForm.amount}
                              onClick={async () => {
                                setProofSubmitting(true);
                                try {
                                  await api.post(`/store/${subdomain}/payment-confirmation`, {
                                    order_number: donation.donation_number, customer_email: donation.donor_email,
                                    bank_name: proofForm.bank_name, account_name: proofForm.account_name,
                                    amount: parseInt(proofForm.amount), transfer_date: proofForm.transfer_date,
                                    proof_url: proofForm.proof_url, notes: proofForm.notes || ''
                                  });
                                  setProofSubmitted(true);
                                  toast.success('Konfirmasi pembayaran terkirim!');
                                } catch (e) { toast.error(e.response?.data?.detail || 'Gagal mengirim konfirmasi'); }
                                finally { setProofSubmitting(false); }
                              }}
                            >
                              {proofSubmitting ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Upload size={16} className="mr-2" />}
                              Kirim Konfirmasi
                            </Button>
                          </div>
                        ) : (
                          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 text-center">
                            <CheckCircle size={40} className="text-emerald-500 mx-auto mb-3" />
                            <p className="font-semibold text-emerald-800 text-lg">Konfirmasi Terkirim!</p>
                            <p className="text-sm text-emerald-600 mt-1">Penjual akan memverifikasi pembayaran Anda</p>
                          </div>
                        )}
                      </div>
                    ) : donationPaymentUrl ? (
                      /* Pakasir payment (QRIS/VA/E-Wallet) */
                      <div className="space-y-4">
                        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-5 space-y-4">
                          <div className="flex items-center gap-2 text-blue-800">
                            <CreditCard size={18} />
                            <p className="font-semibold">Selesaikan Pembayaran</p>
                          </div>
                          <p className="text-sm text-slate-600">
                            Metode: <strong>{{ qris: 'QRIS', va: 'Virtual Account', ewallet: 'E-Wallet' }[donation.payment_method] || donation.payment_method}</strong>
                          </p>
                          <a href={donationPaymentUrl} target="_blank" rel="noreferrer" className="block">
                            <Button className="w-full h-12 text-base font-bold rounded-xl shadow-lg" style={{ backgroundColor: pc }}>
                              <CreditCard size={18} className="mr-2" /> Bayar Sekarang — Rp {fmt(donation.amount)}
                            </Button>
                          </a>
                          <div className="flex items-center gap-4 justify-center text-xs text-blue-600">
                            <span className="flex items-center gap-1"><Clock size={12} /> 24 jam untuk bayar</span>
                            <span className="flex items-center gap-1"><Shield size={12} /> Aman & terenkripsi</span>
                          </div>
                        </div>
                        <div className="bg-slate-50 rounded-2xl p-5 text-sm text-slate-600 space-y-2">
                          <p className="font-semibold text-slate-700 flex items-center gap-2"><ChevronRight size={16} style={{ color: pc }} /> Langkah Pembayaran</p>
                          <ol className="list-decimal list-inside space-y-1.5 ml-1">
                            <li>Klik tombol <strong>"Bayar Sekarang"</strong> di atas</li>
                            <li>Ikuti instruksi pembayaran {{ qris: 'QRIS', va: 'Virtual Account', ewallet: 'E-Wallet' }[donation.payment_method] || ''}</li>
                            <li>Status donasi akan otomatis terupdate</li>
                            <li>Anda akan menerima notifikasi konfirmasi</li>
                          </ol>
                        </div>
                      </div>
                    ) : null}
                  </>
                )}

                {/* Inspirational quote */}
                <div className="bg-gradient-to-r from-pink-50 to-rose-50 rounded-2xl p-5 text-center border border-pink-100">
                  <p className="text-slate-600 italic text-sm leading-relaxed">
                    "Sedekah tidak akan mengurangi harta. Tidaklah seorang hamba memberi maaf kecuali Allah akan menambahkan kemuliaan baginya."
                  </p>
                  <p className="text-xs text-slate-400 mt-2">— HR. Muslim</p>
                </div>
              </CardContent>
            </Card>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to={`/store/${subdomain}`}>
                <Button variant="outline" size="lg" className="w-full sm:w-auto rounded-xl border-pink-200 hover:bg-pink-50">
                  <ShoppingBag size={16} className="mr-2" /> Kunjungi Toko
                </Button>
              </Link>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // ===================== ORDER THANK YOU =====================
  const o = order.order || {};
  const items = o.items || [];
  const bankAccounts = order.bank_accounts || [];
  const paymentMethodLabels = { qris: 'QRIS', va: 'Virtual Account', ewallet: 'E-Wallet', cod: 'COD', bank_transfer: 'Transfer Bank' };
  const payMethod = o.is_cod ? 'COD' : (paymentMethodLabels[o.payment_method] || o.payment_method || '-');
  const isPaid = o.payment_status === 'paid' || o.status === 'processing' || o.status === 'shipped' || o.status === 'delivered';

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      {/* Floating confetti */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="absolute animate-bounce" style={{
            left: `${10 + i * 12}%`, top: `${5 + (i % 4) * 20}%`,
            animationDelay: `${i * 0.2}s`, animationDuration: `${2 + i * 0.3}s`
          }}>
            <Star size={14 + i * 3} className="text-emerald-200/50" />
          </div>
        ))}
      </div>

      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-emerald-100 sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to={`/store/${subdomain}`} className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors">
            <ArrowLeft size={20} />
            <span className="font-medium">{store?.name}</span>
          </Link>
          <Badge variant="outline" className="text-emerald-600 border-emerald-200 gap-1.5">
            <FileText size={14} /> #{o.order_number}
          </Badge>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8 relative z-10">
        <div className="space-y-6">

          {/* ===== Hero Success Banner ===== */}
          <Card className="border-0 shadow-2xl overflow-hidden rounded-3xl">
            <div className="bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-500 p-8 text-center text-white relative overflow-hidden">
              {/* Background pattern */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-4 left-8 w-32 h-32 rounded-full border-4 border-white" />
                <div className="absolute bottom-4 right-12 w-24 h-24 rounded-full border-4 border-white" />
                <div className="absolute top-12 right-8 w-16 h-16 rounded-full border-4 border-white" />
              </div>
              <div className="relative z-10">
                <div className="w-24 h-24 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center mx-auto mb-5">
                  <CheckCircle size={48} className="text-white" />
                </div>
                <h1 className="text-3xl md:text-4xl font-bold mb-2" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                  Pesanan Berhasil!
                </h1>
                <p className="text-white/90 text-lg">Terima kasih atas pesanan Anda, {o.customer_name?.split(' ')[0]}</p>
                <div className="mt-4 inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-full px-5 py-2 text-sm">
                  <FileText size={16} />
                  Order #{o.order_number}
                </div>
              </div>
            </div>

            {/* Quick Summary Bar */}
            <CardContent className="p-0">
              <div className="grid grid-cols-3 divide-x divide-slate-100">
                <div className="p-4 text-center">
                  <p className="text-xs text-slate-500 mb-1">Total</p>
                  <p className="text-lg font-bold" style={{ color: pc }}>Rp {fmt(o.total)}</p>
                </div>
                <div className="p-4 text-center">
                  <p className="text-xs text-slate-500 mb-1">Pembayaran</p>
                  <p className="text-sm font-semibold text-slate-700">{payMethod}</p>
                </div>
                <div className="p-4 text-center">
                  <p className="text-xs text-slate-500 mb-1">Status</p>
                  <Badge className={isPaid ? 'bg-emerald-100 text-emerald-700' : o.is_cod ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}>
                    {isPaid ? 'Dibayar' : o.is_cod ? 'COD' : 'Menunggu'}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ===== Payment Instructions ===== */}
          {!isPaid && (
            <Card className="border-0 shadow-xl rounded-2xl overflow-hidden">
              <div className="h-1.5 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500" />
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  {o.is_cod ? <Truck size={20} className="text-amber-500" /> : <CreditCard size={20} style={{ color: pc }} />}
                  Instruksi Pembayaran
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {o.is_cod ? (
                  <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 space-y-3">
                    <div className="flex items-center gap-3 text-amber-800">
                      <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                        <Truck size={20} className="text-amber-600" />
                      </div>
                      <div>
                        <p className="font-semibold">Bayar di Tempat (COD)</p>
                        <p className="text-sm text-amber-600">Siapkan uang untuk dibayarkan ke kurir</p>
                      </div>
                    </div>
                    <div className="bg-amber-100/50 rounded-xl p-4 space-y-2 text-sm text-amber-800">
                      <div className="flex items-start gap-2"><span className="font-bold text-amber-500">1.</span> Pesanan akan diproses & dikirim penjual</div>
                      <div className="flex items-start gap-2"><span className="font-bold text-amber-500">2.</span> Siapkan uang tunai <strong>Rp {fmt(o.total)}</strong></div>
                      <div className="flex items-start gap-2"><span className="font-bold text-amber-500">3.</span> Cek kondisi barang sebelum membayar</div>
                      <div className="flex items-start gap-2"><span className="font-bold text-amber-500">4.</span> Bayar ke kurir & konfirmasi penerimaan</div>
                    </div>
                  </div>
                ) : order.payment_provider === 'bank_transfer' ? (
                  <div className="space-y-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5">
                      <div className="flex items-center gap-2 text-blue-800 mb-4">
                        <CreditCard size={18} />
                        <p className="font-semibold">Transfer ke Rekening</p>
                      </div>
                      <div className="space-y-3">
                        {bankAccounts.map((acc, i) => (
                          <div key={i} className="bg-white rounded-xl p-4 border border-blue-100 hover:shadow-md transition-shadow">
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-bold text-blue-900 text-lg">{acc.bank_name}</span>
                              <button onClick={() => { navigator.clipboard.writeText(acc.account_number); toast.success('Nomor rekening disalin'); }} className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 px-2.5 py-1 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
                                <Copy size={12} /> Salin
                              </button>
                            </div>
                            <p className="font-mono text-xl tracking-wider font-bold text-slate-900">{acc.account_number}</p>
                            <p className="text-sm text-slate-500 mt-1">a.n. {acc.account_holder}</p>
                          </div>
                        ))}
                      </div>
                      <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center gap-2 text-sm text-amber-700">
                        <AlertCircle size={16} className="flex-shrink-0" />
                        <span>Transfer tepat <strong>Rp {fmt(o.total)}</strong> agar verifikasi lebih cepat</span>
                      </div>
                    </div>

                    {/* Payment Proof Form */}
                    {!proofSubmitted ? (
                      <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 space-y-4">
                        <div className="flex items-center gap-2 text-slate-700">
                          <Upload size={18} />
                          <p className="font-semibold">Konfirmasi Pembayaran</p>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div><Label className="text-xs text-slate-500">Bank Pengirim</Label><Input placeholder="BCA, BNI, BRI..." value={proofForm.bank_name} onChange={e => setProofForm({...proofForm, bank_name: e.target.value})} className="mt-1" /></div>
                          <div><Label className="text-xs text-slate-500">Nama Pengirim</Label><Input placeholder="Nama di rekening" value={proofForm.account_name} onChange={e => setProofForm({...proofForm, account_name: e.target.value})} className="mt-1" /></div>
                          <div><Label className="text-xs text-slate-500">Jumlah Transfer</Label><Input type="number" placeholder={String(o.total)} value={proofForm.amount} onChange={e => setProofForm({...proofForm, amount: e.target.value})} className="mt-1" /></div>
                          <div><Label className="text-xs text-slate-500">Tanggal Transfer</Label><Input type="date" value={proofForm.transfer_date} onChange={e => setProofForm({...proofForm, transfer_date: e.target.value})} className="mt-1" /></div>
                        </div>
                        <div><Label className="text-xs text-slate-500">URL Bukti Transfer (opsional)</Label><Input placeholder="https://... link gambar bukti" value={proofForm.proof_url} onChange={e => setProofForm({...proofForm, proof_url: e.target.value})} className="mt-1" /></div>
                        <Button
                          className="w-full h-11 rounded-xl font-semibold"
                          style={{ backgroundColor: pc }}
                          disabled={proofSubmitting || !proofForm.bank_name || !proofForm.account_name || !proofForm.amount}
                          onClick={async () => {
                            setProofSubmitting(true);
                            try {
                              await api.post(`/store/${subdomain}/payment-confirmation`, {
                                order_number: o.order_number, customer_email: o.customer_email,
                                bank_name: proofForm.bank_name, account_name: proofForm.account_name,
                                amount: parseInt(proofForm.amount), transfer_date: proofForm.transfer_date,
                                proof_url: proofForm.proof_url, notes: proofForm.notes
                              });
                              setProofSubmitted(true);
                              toast.success('Konfirmasi pembayaran terkirim!');
                            } catch (e) { toast.error(e.response?.data?.detail || 'Gagal mengirim konfirmasi'); }
                            finally { setProofSubmitting(false); }
                          }}
                        >
                          {proofSubmitting ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Upload size={16} className="mr-2" />}
                          Kirim Konfirmasi
                        </Button>
                      </div>
                    ) : (
                      <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 text-center">
                        <CheckCircle size={40} className="text-emerald-500 mx-auto mb-3" />
                        <p className="font-semibold text-emerald-800 text-lg">Konfirmasi Terkirim!</p>
                        <p className="text-sm text-emerald-600 mt-1">Penjual akan memverifikasi pembayaran Anda</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {order.payment_url && (
                      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-5 space-y-4">
                        <div className="flex items-center gap-2 text-blue-800">
                          <CreditCard size={18} />
                          <p className="font-semibold">Selesaikan Pembayaran</p>
                        </div>
                        <a href={order.payment_url} target="_blank" rel="noreferrer" className="block">
                          <Button className="w-full h-12 text-base font-bold rounded-xl shadow-lg" style={{ backgroundColor: pc }}>
                            <CreditCard size={18} className="mr-2" /> Bayar Sekarang — Rp {fmt(o.total)}
                          </Button>
                        </a>
                        <div className="flex items-center gap-4 justify-center text-xs text-blue-600">
                          <span className="flex items-center gap-1"><Clock size={12} /> 24 jam untuk bayar</span>
                          <span className="flex items-center gap-1"><Shield size={12} /> Aman & terenkripsi</span>
                        </div>
                      </div>
                    )}
                    <div className="bg-slate-50 rounded-2xl p-5 text-sm text-slate-600 space-y-2">
                      <p className="font-semibold text-slate-700 flex items-center gap-2"><ChevronRight size={16} style={{ color: pc }} /> Langkah Pembayaran</p>
                      <ol className="list-decimal list-inside space-y-1.5 ml-1">
                        <li>Klik tombol <strong>"Bayar Sekarang"</strong> di atas</li>
                        <li>Pilih metode pembayaran ({payMethod})</li>
                        <li>Ikuti instruksi pembayaran</li>
                        <li>Status pesanan akan otomatis terupdate</li>
                        <li>Anda akan menerima notifikasi konfirmasi</li>
                      </ol>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* ===== Order Detail ===== */}
          <Card className="border-0 shadow-xl rounded-2xl overflow-hidden">
            <div className="h-1 bg-gradient-to-r from-emerald-400 to-teal-400" />
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Package size={20} style={{ color: pc }} /> Detail Pesanan
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Order Meta */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-slate-50 rounded-xl p-3">
                  <p className="text-xs text-slate-500 mb-1">Nomor Pesanan</p>
                  <p className="font-mono font-bold text-sm" style={{ color: pc }}>{o.order_number}</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-3">
                  <p className="text-xs text-slate-500 mb-1">Invoice</p>
                  <p className="font-mono font-bold text-sm">{order.invoice_number || '-'}</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-3">
                  <p className="text-xs text-slate-500 mb-1">Tanggal</p>
                  <p className="font-medium text-sm">{new Date(o.created_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-3">
                  <p className="text-xs text-slate-500 mb-1">Status</p>
                  <Badge className={isPaid ? 'bg-emerald-100 text-emerald-700' : o.is_cod ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}>
                    {isPaid ? 'Sudah Dibayar' : o.is_cod ? 'Menunggu Konfirmasi COD' : 'Menunggu Pembayaran'}
                  </Badge>
                </div>
              </div>

              {/* Items */}
              <div className="border-t border-slate-100 pt-4">
                <p className="text-sm font-semibold text-slate-700 mb-3">Produk yang Dibeli</p>
                <div className="space-y-3">
                  {items.map((item, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                      <div className="w-12 h-12 rounded-lg bg-white border border-slate-200 flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {item.image ? (
                          <img src={item.image} alt={item.product_name} className="w-full h-full object-cover" />
                        ) : (
                          <Package size={20} className="text-slate-300" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 text-sm truncate">{item.product_name}</p>
                        <p className="text-xs text-slate-500">{item.quantity}x @ Rp {fmt(item.price || Math.round(item.total / item.quantity))}</p>
                      </div>
                      <p className="font-semibold text-sm" style={{ color: pc }}>Rp {fmt(item.total)}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Price Summary */}
              <div className="border-t border-slate-100 pt-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Subtotal</span>
                  <span className="font-medium">Rp {fmt(o.subtotal)}</span>
                </div>
                {o.discount > 0 && (
                  <div className="flex justify-between text-emerald-600">
                    <span>Diskon {o.voucher_code ? `(${o.voucher_code})` : ''}</span>
                    <span>-Rp {fmt(o.discount)}</span>
                  </div>
                )}
                {o.shipping_cost > 0 && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Ongkir {o.shipping_courier ? `(${o.shipping_courier})` : ''}</span>
                    <span>Rp {fmt(o.shipping_cost)}</span>
                  </div>
                )}
                {o.cod_fee > 0 && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Biaya COD</span>
                    <span>Rp {fmt(o.cod_fee)}</span>
                  </div>
                )}
                <div className="border-t border-slate-200 pt-3 flex justify-between items-center">
                  <span className="font-bold text-base text-slate-900">Total Pembayaran</span>
                  <span className="text-xl font-bold" style={{ color: pc }}>Rp {fmt(o.total)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ===== Customer Info ===== */}
          <Card className="border-0 shadow-xl rounded-2xl overflow-hidden">
            <div className="h-1 bg-gradient-to-r from-purple-400 to-pink-400" />
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Mail size={20} className="text-purple-500" /> Informasi Pembeli
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                  <div className="w-9 h-9 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                    <Star size={16} className="text-purple-500" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Nama</p>
                    <p className="font-medium text-sm text-slate-900">{o.customer_name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                  <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <Mail size={16} className="text-blue-500" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Email</p>
                    <p className="font-medium text-sm text-slate-900">{o.customer_email}</p>
                  </div>
                </div>
                {o.customer_phone && (
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                    <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <Phone size={16} className="text-emerald-500" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Telepon</p>
                      <p className="font-medium text-sm text-slate-900">{o.customer_phone}</p>
                    </div>
                  </div>
                )}
                {o.destination_label && (
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                    <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <MapPin size={16} className="text-amber-500" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Alamat Pengiriman</p>
                      <p className="font-medium text-sm text-slate-900">{o.destination_label}</p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* ===== What's Next Timeline ===== */}
          <Card className="border-0 shadow-xl rounded-2xl overflow-hidden">
            <div className="h-1 bg-gradient-to-r from-amber-400 to-orange-400" />
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock size={20} className="text-amber-500" /> Selanjutnya
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {[
                  { step: 1, title: isPaid ? 'Pembayaran Diterima' : 'Selesaikan Pembayaran', desc: isPaid ? 'Pembayaran Anda sudah dikonfirmasi' : o.is_cod ? 'Bayar saat barang tiba' : 'Lakukan pembayaran sesuai instruksi', done: isPaid, icon: CreditCard, color: 'emerald' },
                  { step: 2, title: 'Pesanan Diproses', desc: 'Penjual akan menyiapkan pesanan Anda', done: ['processing', 'shipped', 'delivered'].includes(o.status), icon: Package, color: 'blue' },
                  { step: 3, title: 'Pesanan Dikirim', desc: 'Anda akan mendapat notifikasi & nomor resi', done: ['shipped', 'delivered'].includes(o.status), icon: Truck, color: 'purple' },
                  { step: 4, title: 'Pesanan Sampai', desc: 'Konfirmasi penerimaan barang', done: o.status === 'delivered', icon: Gift, color: 'pink' },
                ].map(({ step, title, desc, done, icon: Icon, color }) => (
                  <div key={step} className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${done ? `bg-${color}-100` : 'bg-slate-100'}`}>
                      {done ? <CheckCircle size={20} className={`text-${color}-500`} /> : <Icon size={20} className="text-slate-400" />}
                    </div>
                    <div className="flex-1 pb-4 border-b border-slate-50 last:border-0">
                      <p className={`font-medium ${done ? 'text-slate-900' : 'text-slate-500'}`}>{title}</p>
                      <p className="text-sm text-slate-400">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* ===== Action Buttons ===== */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center pb-8">
            {order.payment_url && !isPaid && !o.is_cod && (
              <a href={order.payment_url} target="_blank" rel="noreferrer">
                <Button className="w-full sm:w-auto h-12 text-base font-semibold rounded-xl shadow-lg" style={{ backgroundColor: pc }}>
                  <CreditCard size={18} className="mr-2" /> Bayar Sekarang
                </Button>
              </a>
            )}
            <Link to={`/store/${subdomain}`}>
              <Button variant="outline" size="lg" className="w-full sm:w-auto rounded-xl border-emerald-200 hover:bg-emerald-50 h-12">
                <ShoppingBag size={18} className="mr-2" /> Lanjut Belanja
              </Button>
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
